import { buildSalaryMultiplierTypedAmount } from "./salaryMultiplierTypedAmount";
import { definitionOfEarningsSpec, rateGuarantee } from "./sharedTypedAmount";

import type {
  TypedAmountSpec,
  TypedAmountTypeOption,
} from "../typedAmountTypes";

// Type values reused across options and the multiplier/additional gating lists.
const DOLLAR_AMOUNT = "dollarAmount";
const SALARY_MULTIPLIER = "salaryMultiplier";
const SALARY_MULTIPLIER_PLUS_DOLLAR = "salaryMultiplierPlusDollarAmount";
const LESSER_OF_DOLLAR_OR_MULTIPLIER = "lesserOfDollarAmountOrSalaryMultiplier";
const SALARY_MULTIPLIER_WITH_CAP = "salaryMultiplierWithCap";
const SALARY_MULTIPLIER_WITH_ADDITIONAL =
  "salaryMultiplierWithDollarAmountPlusAdditionalAmount";

const DOLLAR_AMOUNT_LABEL = "Dollar Amount";

const NOT_INCLUDED: TypedAmountTypeOption = {
  value: "notIncluded",
  label: "Not Included",
  hideAmount: true,
};
const NOT_DISCLOSED: TypedAmountTypeOption = {
  value: "notDisclosed",
  label: "Not Disclosed",
  hideAmount: true,
};
const DOLLAR_ONLY_OPTIONS: TypedAmountTypeOption[] = [
  { value: DOLLAR_AMOUNT, label: "Dollar amount" },
  NOT_INCLUDED,
  NOT_DISCLOSED,
];

// Benefit Amount / Maximum / Guarantee Issue each pair a dollar amount with a
// salary multiplier (and, for Guarantee Issue, an additional amount). Types
// with no dollar input hide the amount; the multiplier/additional inputs are
// gated to the composite types below.
const BENEFIT_AMOUNT_OPTIONS: TypedAmountTypeOption[] = [
  { value: "increments", label: "Increments" },
  { value: DOLLAR_AMOUNT, label: DOLLAR_AMOUNT_LABEL },
  { value: SALARY_MULTIPLIER, label: "Salary Multiplier", hideAmount: true },
  {
    value: SALARY_MULTIPLIER_PLUS_DOLLAR,
    label: "Salary Multiplier Plus Dollar Amount",
  },
  NOT_DISCLOSED,
];

const BENEFIT_MAXIMUM_OPTIONS: TypedAmountTypeOption[] = [
  { value: DOLLAR_AMOUNT, label: DOLLAR_AMOUNT_LABEL },
  { value: SALARY_MULTIPLIER, label: "Salary Multiplier", hideAmount: true },
  {
    value: LESSER_OF_DOLLAR_OR_MULTIPLIER,
    label: "Lesser of Dollar Amount or Salary Multiplier",
  },
  NOT_DISCLOSED,
];

const GUARANTEE_ISSUE_OPTIONS: TypedAmountTypeOption[] = [
  { value: DOLLAR_AMOUNT, label: DOLLAR_AMOUNT_LABEL },
  { value: SALARY_MULTIPLIER_WITH_CAP, label: "Salary Multiplier With Cap" },
  {
    value: SALARY_MULTIPLIER_WITH_ADDITIONAL,
    label: "Salary Multiplier With Dollar Amount Plus Additional Amount",
  },
  {
    value: "allGuaranteeIssue",
    label: "All Guarantee Issue",
    hideAmount: true,
  },
  NOT_INCLUDED,
  NOT_DISCLOSED,
];

export const basicLifeTypedAmountFields: Record<string, TypedAmountSpec> = {
  benefitAmount: buildSalaryMultiplierTypedAmount({
    typeKey: "benefitAmountType",
    dollarKey: "benefitAmountDollarAmount",
    multiplierKey: "benefitAmountSalaryMultiplier",
    typeOptions: BENEFIT_AMOUNT_OPTIONS,
    multiplierTypes: [SALARY_MULTIPLIER, SALARY_MULTIPLIER_PLUS_DOLLAR],
  }),
  benefitMaximum: buildSalaryMultiplierTypedAmount({
    typeKey: "benefitMaximumType",
    dollarKey: "benefitMaximumDollarAmount",
    multiplierKey: "benefitMaximumSalaryMultiplier",
    typeOptions: BENEFIT_MAXIMUM_OPTIONS,
    multiplierTypes: [SALARY_MULTIPLIER, LESSER_OF_DOLLAR_OR_MULTIPLIER],
  }),
  guaranteeIssue: buildSalaryMultiplierTypedAmount({
    typeKey: "guaranteeIssueType",
    dollarKey: "guaranteeIssueDollarAmount",
    multiplierKey: "guaranteeIssueSalaryMultiplier",
    additionalKey: "guaranteeIssueAdditionalAmount",
    typeOptions: GUARANTEE_ISSUE_OPTIONS,
    multiplierTypes: [
      SALARY_MULTIPLIER_WITH_CAP,
      SALARY_MULTIPLIER_WITH_ADDITIONAL,
    ],
    additionalTypes: [SALARY_MULTIPLIER_WITH_ADDITIONAL],
  }),
  spouseBenefit: {
    typeKey: "spouseBenefitType",
    amountKey: "spouseBenefitAmount",
    amountLabel: "Amount",
    typeOptions: DOLLAR_ONLY_OPTIONS,
  },
  childBenefit: {
    typeKey: "childBenefitType",
    amountKey: "childBenefitAmount",
    amountLabel: "Amount",
    typeOptions: DOLLAR_ONLY_OPTIONS,
  },
  definitionOfEarnings: definitionOfEarningsSpec,
  rateGuarantee,
};
